<template>
  <div
    class="boxes-table"
    style="height: 100%;">

    <el-divider v-if="!tableIsNotEmpty"/>
    <div
      v-if="!tableIsNotEmpty"
      class="boxes-table-no-data-container">
      <div class="boxes-table-no-data-container-body">
        <div class="boxes-table-no-data-image" />
        <div class="mt-24 el-text el-text--display-regular">
          {{ noContentMessage }}
        </div>
      </div>
    </div>

    <el-table
      v-if="tableIsNotEmpty"
      style="height: 100%;"
      height="fit-scrollable"
      :data="tableItems"
      highlight-current-row
      :default-sort = "{ prop: 'displayName' }"
      :loading-append="isFetchBoxesInProgress"
      @row-click="onTableRowClick"
      @row-dblclick="onTableRowDblClick"
      ref="table"
      border-bottom>

      <el-table-column
        prop="id"
        label="id"
        hidden/>

      <el-table-column
        prop="displayName"
        :label="$t('wrh-dashboard.backups-table.column.name')"
        align="left"
        sortable>
        <template slot-scope="{ row }">
          <el-icon
            :style="!row.options.browsable ? 'opacity: 0.35' : ''"
            :name="'stub' | archiveIcon(row.options.notarized ? 'NOTARIZED' : row.type)"/>
          <span class="ml-16">
            {{ row.displayName }}
          </span>
        </template>
      </el-table-column>

      <el-table-column
        prop="extension"
        label="extension"
        hidden/>

      <el-table-column
        prop="status"
        align="right"
        width="96"
        label="">
        <template slot-scope="{ row }">
          <div
            v-if="row.options.notarized"
            class="mr-16">
            <el-icon
              slot="icon"
              color="brand-light"
              name="i-license--16"/>
          </div>
          <div
            v-if="row.options.passwordRequired"
            class="mr-16">
            <el-icon
              slot="icon"
              color="brand-light"
              name="i-internet-access--16"/>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        prop="size"
        align="right"
        width="180"
        :label="$t('wrh-dashboard.backups-table.column.size')"
        :sort-by="row => row.size"
        sortable>
        <template slot-scope="{ row }">
          {{ row.size | size }}
        </template>
      </el-table-column>

      <el-table-column
        prop="lastVersion"
        align="left"
        width="280"
        :label="$t('wrh-dashboard.backups-table.column.version')"
        :sort-by="row => new Date(row.lastVersion).getTime()"
        sortable>
        <template slot-scope="{ row }">
          {{ row.lastVersion | date }}
        </template>
      </el-table-column>
    </el-table>

    <password-dialog-box
      v-if="passwordDialogBoxVisible"
      :archive-id="selectedBox.id"
      :box-group="selectedBox.group"
      :archive-display-name="selectedBox.displayName"
      :payload="passwordDialogBoxPayload"
      @handleSubmit="handlePasswordSubmit"
      @handleClose="handlePasswordClose"
    />

    <delete-archive-dialog-box
      v-if="deleteDialogBoxVisible"
      :archive-id="selectedBox.id"
      :box-group="selectedBox.group"
      @handleClose="handleDeleteClose"
    />

    <el-details-layout
      class="el-sidebar"
      v-if="isSideBarOpen"
      open
      @close="sidebarClose()">

      <template #details>
        <el-details-panel
          @command="handleSidebarCommand"
          :title="sidebarTitle"
          show-close
          :actions="sidebarActions"
          :properties="sidebarProps">
          <template
            #preview
            v-if="isSideBarPreviewEnabled">
            <div class="el-sidebar-preview">
              <el-icon
                class="el-sidebar-icon"
                :name="sidebarPreviewIconName"/>
            </div>
          </template>
        </el-details-panel>
      </template>
    </el-details-layout>


  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import PasswordDialogBox from './PasswordDialogBox';
import DeleteArchiveDialogBox from './DeleteArchiveDialogBox';
import CleanupComponent from './CleanupComponent';

import {
  PENDING_STATUS
} from '../store/action-status';

import backupType from '../store/backup-types';
import backupEventCategory from '../store/backup-event-category';

const { mapGetters } = createNamespacedHelpers('wrh-dashboard');

export default {
  name: 'BoxesTable',
  components: {
    PasswordDialogBox,
    DeleteArchiveDialogBox,
    CleanupComponent
  },
  props: {
    boxList: {
      type: Array,
      default: () => [],
      required: true
    },
    selBoxGroup: {
      type: String,
      default: '',
      required: true
    }
  },
  data() {
    return {
      selectedBox: {},
      passwordDialogBoxPayload: {},
      passwordDialogBoxVisible: false,
      deleteDialogBoxVisible: false,
      isCleanupComponentVisible: false,
      selectedItems: [],
      noContentMessage: ''
    };
  },
  computed: {
    ...mapGetters([
      'getFetchNavMenuItemsStatus',
      'getActiveNavMenuItemId'
    ]),
    isFetchBoxesInProgress() {
      return this.getFetchNavMenuItemsStatus === PENDING_STATUS;
    },
    isSideBarOpen() {
      return this.selectedItems.length > 0;
    },
    isSideBarPreviewEnabled() {
      return this.selectedItems.length === 1;
    },
    sidebarPreviewIconName() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.$options.filters.archiveIcon('stub', row.options.notarized ? 'NOTARIZED' : row.type);
      }
      return '';
    },
    sidebarTitle() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getSidebarBackupNameField(row.displayName, row.extension);
      }
      return this.$tc(
        'wrh-dashboard.side-bar.selected.title:plural',
        this.selectedItems.length, { count: this.selectedItems.length }
      );
    },
    sidebarProps() {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return [
          {
            label: this.$t('wrh-dashboard.backups-sidebar.label.size'),
            value: this.$options.filters.size(row.size)
          },
          {
            label: this.$t('wrh-dashboard.backups-sidebar.label.type'),
            value: this.getBackupTypeText(row.type, row.options.notarized)
          },
          {
            label: this.$t('wrh-dashboard.backups-sidebar.label.version'),
            value: this.$options.filters.date(row.lastVersion)
          }
        ];
      }
      return [];
    },
    sidebarActions() {
      const actions = [];
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        return this.getMenuActions(row);
      }
      return actions;
    },
    tableItems() {
      return this.getSelectedOverviewBoxList(this.selBoxGroup);
    },
    tableIsNotEmpty() {
      return this.isFetchBoxesInProgress || this.tableItems.length > 0;
    }
  },
  watch: {
    getFetchNavMenuItemsStatus(status) {
      if (status === PENDING_STATUS) {
        this.sidebarClose();
      }
    },
    getActiveNavMenuItemId(itemId) {
      this.sidebarClose();
    }
  },
  methods: {
    getSidebarBackupNameField(displayName, extension) {
      if (displayName.toLowerCase().indexOf(extension.toLowerCase()) !== -1) {
        return displayName;
      }
      return displayName + extension;
    },
    getBackupTypeText(type, isNotarized) {
      return this.$t(backupType(type, isNotarized).i18nKey);
    },
    updateNoContentMessage(group) {
      if (group === 'SYNC') {
        this.noContentMessage = this.$t('wrh-dashboard.backups-table.message.no-syncs');
      } else if (group === 'ARCHIVES') {
        this.noContentMessage = this.$t('wrh-dashboard.backups-table.message.no-archives');
      } else {
        this.noContentMessage = this.$t('wrh-dashboard.backups-table.message.no-backups');
      }
    },
    sidebarClose() {
      this.$refs.table.clearSelection();
      this.selectedItems = [];
    },
    getMenuActions(row) {
      const actions = [];

      if (row.options.cleanupEnabled && row.group === 'BACKUPS') {
        actions.push({
          label: this.$t('wrh-dashboard.backups-table.action.cleanup'),
          command: 'cleanup',
          icon: 'i-clean-up-o--16'
        });
      }
      if (row.options.deletionEnabled) {
        actions.push({
          label: this.$t('wrh-dashboard.backups-table.action.delete'),
          command: 'delete',
          icon: 'i-trash-o--16'
        });
      }
      return actions;
    },
    getSelectedOverviewBoxList(boxGroup) {
      this.updateNoContentMessage(boxGroup);
      return this.boxList.filter(box => box.group === boxGroup);
    },
    handleSidebarCommand(command) {
      if (this.selectedItems.length === 1) {
        const row = this.selectedItems[0];
        this.handleAction(command, row);
      } else {
        console.log('group operation');
      }
      this.sidebarClose();
    },
    handleAction(command, tableRow) {
      if (command === 'cleanup') {
        if (this.isPasswordRequired(tableRow.id, 'cleanup')) {
          this.showPasswordDialogBox(true);
        } else {
          this.selectedBox = this.getSelectedBox(tableRow.id);
          this.cleanupSelectedBackup(this.selectedBox);
        }
      } else if (command === 'delete') {
        this.selectedBox = this.getSelectedBox(tableRow.id);
        this.showDeleteArchiveDialogBox(true);

        this.$gtm.push({
          event: 'Acronis',
          eventCategory: backupEventCategory(this.selBoxGroup),
          eventAction: 'Delete_open',
          eventLabel: ''
        });
      }
    },
    onTableRowClick(row, event, column) {
      if (column.prop === 'displayName') {
        this.openBox(row);
      } else {
        this.$refs.table.clearSelection();
        this.selectedItems = [row];
      }
    },
    onTableRowDblClick(row) {
      this.openBox(row);
    },
    openBox(row) {
      // this.$refs.table.clearSelection();
      // this.$refs.table.$emit('selection-change', []);
      this.sidebarClose();
      if (this.isBrowsable(row.id)) {
        if (this.isPasswordRequired(row.id, 'open')) {
          this.showPasswordDialogBox(true);
        } else {
          this.emitBoxSelectedEvent(row.id);
        }
      }
    },
    isPasswordRequired(boxId, action) {
      this.selectedBox = this.getSelectedBox(boxId);
      this.passwordDialogBoxPayload = { command: action };
      return this.selectedBox.options.passwordRequired === true;
    },
    isBrowsable(boxId) {
      const box = this.getSelectedBox(boxId);
      return box.options.browsable !== false;
    },
    getSelectedBox(boxId) {
      return this.boxList.filter(obj => obj.id === boxId)[0];
    },
    showPasswordDialogBox(show) {
      this.passwordDialogBoxVisible = show;
    },
    handlePasswordSubmit(payload) {
      // console.log(payload.command);
      this.showPasswordDialogBox(false);
      if (payload.command === 'open') {
        this.emitBoxSelectedEvent(this.selectedBox.id);
      } else if (payload.command === 'cleanup') {
        this.selectedBox = this.getSelectedBox(this.selectedBox.id);
        this.cleanupSelectedBackup(this.selectedBox);
      }
    },
    handlePasswordClose() {
      this.showPasswordDialogBox(false);
    },
    showDeleteArchiveDialogBox(show) {
      this.deleteDialogBoxVisible = show;
    },
    handleDeleteClose() {
      this.showDeleteArchiveDialogBox(false);
    },
    cleanupSelectedBackup(cleanupBox) {
      this.$gtm.push({
        event: 'Acronis',
        eventCategory: backupEventCategory(this.selBoxGroup),
        eventAction: 'Clean_up_open',
        eventLabel: ''
      });
      this.$emit('cleanupBackup', cleanupBox);
    },
    emitBoxSelectedEvent(id) {
      this.$emit('boxSelected', {
        boxId: id
      });
    }
  }
};
</script>

<style lang="scss" scoped>
  .boxes-table-no-data-container {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }

  .boxes-table-no-data-container-body {
    margin-left: 256px;
    margin-top: -78px;
  }

  .boxes-table-no-data-image {
    margin: auto;
    background: url('../assets/empty/empty-cloud.png') no-repeat;
    width: 128px;
    height: 128px;
  }
</style>
